import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  Fingerprint, 
  Shield, 
  Cloud, 
  DollarSign,
  Users,
  CheckCircle,
  ArrowRight,
  Zap,
  Lock,
  Clock,
  TrendingUp,
  Award,
  Phone,
  Mail,
  ChevronDown,
  ChevronUp,
  Calculator,
  Building2,
  Play
} from 'lucide-react';

const ForVendorsPage = () => {
  const [openFaq, setOpenFaq] = useState(null);

  const benefits = [
    {
      icon: <DollarSign className="w-6 h-6" />,
      title: "Reduce Costs by 70%",
      description: "Replace expensive LiveScan systems ($7,000-$12,000) with our affordable subscription starting at $49/month."
    },
    {
      icon: <Zap className="w-6 h-6" />,
      title: "Start in Minutes",
      description: "No complex installation. Download our app, connect your scanner, and start capturing fingerprints immediately."
    },
    {
      icon: <Cloud className="w-6 h-6" />,
      title: "Cloud or Offline",
      description: "Work anywhere with cloud sync or use offline mode for mobile fingerprinting at client locations."
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: "FBI/CJIS Compliant",
      description: "Meet all federal security requirements with AES-256 encryption, MFA, and full audit logging."
    },
    {
      icon: <Lock className="w-6 h-6" />,
      title: "Your Data, Your Control",
      description: "All fingerprint data is encrypted and stored securely. You maintain full ownership and control."
    },
    {
      icon: <Users className="w-6 h-6" />,
      title: "Grow Your Business",
      description: "Get listed in our provider directory and connect with clients searching for fingerprinting services."
    }
  ];

  const features = [
    "FBI-compliant FD-258 & FD-1164 cards",
    "ID verification with OCR",
    "EBTS electronic submission",
    "WSQ compression",
    "Multi-factor authentication",
    "Client intake forms",
    "Secure PDF sharing",
    "Audit logging",
    "Real-time cloud backup",
    "Offline mode (7-day grace)",
    "Multi-user support",
    "API access (Enterprise)"
  ];

  const testimonials = [
    {
      quote: "FingerprintVS saved us thousands compared to traditional LiveScan. Setup was a breeze and our clients love the professional cards.",
      author: "Maria S.",
      role: "Notary Public, NY",
      rating: 5
    },
    {
      quote: "The offline mode is perfect for my mobile fingerprinting business. I can capture prints anywhere and sync when I'm back online.",
      author: "James T.",
      role: "Mobile Fingerprinting Services, NJ",
      rating: 5
    },
    {
      quote: "Enterprise plan handles all our locations seamlessly. The API integration with our booking system was straightforward.",
      author: "Robert K.",
      role: "Background Check Agency, CT",
      rating: 5
    }
  ];

  const faqs = [
    {
      question: "What scanner hardware do I need?",
      answer: "FingerprintVS works with the Suprema RealScan G10 scanner, which is FBI PIV/FAP60 certified. If you already have one, great! If not, we can recommend authorized resellers."
    },
    {
      question: "Is FingerprintVS FBI certified?",
      answer: "Our system is CJIS-Ready and produces FBI Appendix F compliant fingerprint cards (500 DPI). For direct electronic submission to FBI, you would need to work with an FBI-approved channeler."
    },
    {
      question: "Can I use it offline?",
      answer: "Yes! All plans include offline mode with a 7-day grace period. Perfect for mobile fingerprinting. Your data syncs automatically when you reconnect."
    },
    {
      question: "How is my data protected?",
      answer: "We use AES-256 encryption (military-grade) for all data at rest and TLS 1.2/1.3 for data in transit. We're fully compliant with CJIS Security Policy v6.0."
    },
    {
      question: "Can I cancel anytime?",
      answer: "Monthly plans are month-to-month with no long-term contracts. Annual plans provide savings but are non-refundable after license key delivery."
    },
    {
      question: "Do you offer training?",
      answer: "Yes! We offer one-hour training sessions ($89) and remote setup assistance ($249). Enterprise plans include custom training."
    }
  ];

  const pricingTiers = [
    {
      name: "Basic",
      subtitle: "USB/Offline",
      price: "$49",
      period: "/month",
      annual: "$499/year",
      features: ["1 User", "1 Workstation", "FD-258 Cards", "Offline Mode", "Email Support (48hr)"],
      cta: "Start Free Trial",
      popular: false
    },
    {
      name: "Professional",
      subtitle: "Cloud",
      price: "$99",
      period: "/month",
      annual: "$999/year",
      features: ["5 Users", "5 Workstations", "FD-258 & FD-1164", "EBTS Export", "Cloud Sync", "Priority Support (24hr)"],
      cta: "Start Free Trial",
      popular: true
    },
    {
      name: "Enterprise",
      subtitle: "LAN",
      price: "$399",
      period: "/month",
      annual: "$3,999/year",
      features: ["Unlimited Users", "Unlimited Workstations", "On-Premise Option", "API Access", "White Label", "Phone Support"],
      cta: "Contact Sales",
      popular: false
    }
  ];

  return (
    <div className="min-h-screen bg-slate-900 relative" data-testid="for-vendors-page">
      {/* Background */}
      <div 
        className="fixed inset-0 z-0"
        style={{
          backgroundImage: `url('https://static.prod-images.emergentagent.com/jobs/07e51985-c374-4472-a4ec-7832a182bb43/images/a599bfb20d8b730453411fb0496e41df29ac3936a599b1146f16c28f94161081.png')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed'
        }}
      />
      <div className="fixed inset-0 z-0 bg-slate-900/90" />
      
      {/* Content */}
      <div className="relative z-10">
        {/* Header */}
        <header className="border-b border-white/10 bg-slate-900/50 backdrop-blur-md sticky top-0 z-20">
          <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <Fingerprint className="w-8 h-8 text-blue-400" />
              <span className="text-xl font-bold text-white">FingerprintVS</span>
            </Link>
            <nav className="hidden md:flex items-center gap-6">
              <Link to="/services" className="text-gray-300 hover:text-white transition">For Clients</Link>
              <Link to="/for-vendors" className="text-blue-400 font-medium">For Vendors</Link>
              <Link to="/calculator" className="text-gray-300 hover:text-white transition">Profit Calculator</Link>
              <Link to="/pricing" className="text-gray-300 hover:text-white transition">Pricing</Link>
            </nav>
            <div className="flex items-center gap-3">
              <Link to="/login" className="text-gray-300 hover:text-white transition">Login</Link>
              <Link to="/register" className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg font-medium transition">
                Start Free Trial
              </Link>
            </div>
          </div>
        </header>

        {/* Hero Section */}
        <section className="py-20 px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center max-w-4xl mx-auto">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-500/10 border border-blue-500/30 rounded-full text-blue-400 text-sm font-medium mb-6">
                <Award className="w-4 h-4" />
                FBI/CJIS Compliant Fingerprinting System
              </div>
              
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                Professional Fingerprinting<br />
                <span className="text-blue-400">Without the $10,000 Price Tag</span>
              </h1>
              
              <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
                Start your fingerprinting business or upgrade from expensive LiveScan systems. 
                FBI-compliant cards, cloud storage, and everything you need — from $49/month.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link 
                  to="/register"
                  className="px-8 py-4 bg-blue-500 hover:bg-blue-600 text-white rounded-xl font-semibold text-lg transition flex items-center justify-center gap-2"
                >
                  Start 14-Day Free Trial
                  <ArrowRight className="w-5 h-5" />
                </Link>
                <Link 
                  to="/calculator"
                  className="px-8 py-4 bg-white/10 hover:bg-white/20 text-white rounded-xl font-semibold text-lg transition flex items-center justify-center gap-2"
                >
                  <Calculator className="w-5 h-5" />
                  Calculate Your Profit
                </Link>
              </div>
              
              <p className="text-gray-500 text-sm mt-4">
                No credit card required • Cancel anytime
              </p>
            </div>
          </div>
        </section>

        {/* Benefits Grid */}
        <section className="py-16 px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-white text-center mb-4">
              Why Vendors Choose FingerprintVS
            </h2>
            <p className="text-gray-400 text-center mb-12 max-w-2xl mx-auto">
              Join hundreds of fingerprinting providers who switched from expensive legacy systems.
            </p>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {benefits.map((benefit, idx) => (
                <div 
                  key={idx}
                  className="bg-white/5 border border-white/10 rounded-xl p-6 hover:border-blue-500/30 transition"
                >
                  <div className="w-12 h-12 bg-blue-500/20 rounded-lg flex items-center justify-center text-blue-400 mb-4">
                    {benefit.icon}
                  </div>
                  <h3 className="text-lg font-semibold text-white mb-2">{benefit.title}</h3>
                  <p className="text-gray-400 text-sm">{benefit.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Features List */}
        <section className="py-16 px-4 bg-white/5">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-white text-center mb-12">
              Everything You Need to Run Your Business
            </h2>
            
            <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-4">
              {features.map((feature, idx) => (
                <div key={idx} className="flex items-center gap-2 text-gray-300">
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                  <span>{feature}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Pricing Preview */}
        <section className="py-16 px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-white text-center mb-4">
              Simple, Transparent Pricing
            </h2>
            <p className="text-gray-400 text-center mb-12">
              No hidden fees. No long-term contracts on monthly plans.
            </p>
            
            <div className="grid md:grid-cols-3 gap-6">
              {pricingTiers.map((tier, idx) => (
                <div 
                  key={idx}
                  className={`bg-white/5 border rounded-xl p-6 ${
                    tier.popular 
                      ? 'border-purple-500 ring-2 ring-purple-500/20' 
                      : 'border-white/10'
                  }`}
                >
                  {tier.popular && (
                    <div className="bg-purple-500 text-white text-xs font-semibold px-3 py-1 rounded-full inline-block mb-4">
                      MOST POPULAR
                    </div>
                  )}
                  
                  <h3 className="text-white font-semibold text-lg">{tier.name}</h3>
                  <p className="text-gray-500 text-sm mb-4">{tier.subtitle}</p>
                  
                  <div className="mb-6">
                    <span className="text-3xl font-bold text-white">{tier.price}</span>
                    <span className="text-gray-400">{tier.period}</span>
                    <p className="text-green-400 text-sm">{tier.annual} (save ~15%)</p>
                  </div>
                  
                  <ul className="space-y-2 mb-6">
                    {tier.features.map((feature, i) => (
                      <li key={i} className="flex items-center gap-2 text-gray-300 text-sm">
                        <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                  
                  <Link
                    to={tier.name === 'Enterprise' ? '/contact' : '/register'}
                    className={`block w-full py-3 rounded-lg font-semibold text-center transition ${
                      tier.popular
                        ? 'bg-purple-500 hover:bg-purple-600 text-white'
                        : 'bg-white/10 hover:bg-white/20 text-white'
                    }`}
                  >
                    {tier.cta}
                  </Link>
                </div>
              ))}
            </div>
            
            <div className="text-center mt-8">
              <Link to="/pricing" className="text-blue-400 hover:text-blue-300 transition flex items-center justify-center gap-2">
                View full pricing details
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="py-16 px-4 bg-white/5">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-white text-center mb-12">
              Trusted by Providers Across the Country
            </h2>
            
            <div className="grid md:grid-cols-3 gap-6">
              {testimonials.map((testimonial, idx) => (
                <div key={idx} className="bg-white/5 border border-white/10 rounded-xl p-6">
                  <div className="flex items-center gap-1 mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <svg key={i} className="w-5 h-5 text-yellow-400 fill-current" viewBox="0 0 20 20">
                        <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z"/>
                      </svg>
                    ))}
                  </div>
                  <p className="text-gray-300 mb-4 italic">"{testimonial.quote}"</p>
                  <div>
                    <p className="text-white font-semibold">{testimonial.author}</p>
                    <p className="text-gray-500 text-sm">{testimonial.role}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-16 px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold text-white text-center mb-12">
              Frequently Asked Questions
            </h2>
            
            <div className="space-y-4">
              {faqs.map((faq, idx) => (
                <div 
                  key={idx}
                  className="bg-white/5 border border-white/10 rounded-xl overflow-hidden"
                >
                  <button
                    onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                    className="w-full px-6 py-4 flex items-center justify-between text-left"
                  >
                    <span className="text-white font-medium">{faq.question}</span>
                    {openFaq === idx ? (
                      <ChevronUp className="w-5 h-5 text-gray-400" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-gray-400" />
                    )}
                  </button>
                  {openFaq === idx && (
                    <div className="px-6 pb-4">
                      <p className="text-gray-400">{faq.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="py-20 px-4 bg-gradient-to-r from-blue-900/50 to-purple-900/50">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Ready to Transform Your Business?
            </h2>
            <p className="text-gray-300 text-lg mb-8">
              Start your 14-day free trial today. No credit card required.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link 
                to="/register"
                className="px-8 py-4 bg-blue-500 hover:bg-blue-600 text-white rounded-xl font-semibold text-lg transition flex items-center justify-center gap-2"
              >
                Start Free Trial
                <ArrowRight className="w-5 h-5" />
              </Link>
              <a 
                href="mailto:sales@fingerprintvs.com"
                className="px-8 py-4 bg-white/10 hover:bg-white/20 text-white rounded-xl font-semibold text-lg transition flex items-center justify-center gap-2"
              >
                <Mail className="w-5 h-5" />
                Contact Sales
              </a>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="py-8 px-4 border-t border-white/10">
          <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <Fingerprint className="w-6 h-6 text-blue-400" />
              <span className="text-white font-semibold">FingerprintVS</span>
              <span className="text-gray-500 text-sm">• Fingerprinting Virtual System</span>
            </div>
            <div className="flex items-center gap-6 text-sm text-gray-400">
              <Link to="/services" className="hover:text-white transition">For Clients</Link>
              <Link to="/for-vendors" className="hover:text-white transition">For Vendors</Link>
              <Link to="/pricing" className="hover:text-white transition">Pricing</Link>
              <a href="mailto:support@fingerprintvs.com" className="hover:text-white transition">Contact</a>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default ForVendorsPage;
